'use client'

import { ChangeEvent, MouseEvent, useRef } from 'react'
import { Button, Icon } from '@vinted/web-ui'
import { Plus16 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

type Props = {
  supportedTypes?: string
  maximumFileSize: number
  title?: string | null
  onChange: (file: File | null) => void
  onError: (error: string) => void
}

const FileSelect = ({ supportedTypes, maximumFileSize, title, onChange, onError }: Props) => {
  const baseTranslate = useTranslate()
  const fileSelectTranslate = useTranslate('kyc_form.a11y.identity_document')

  const fileInput = useRef<HTMLInputElement>(null)

  const handleClick = () => {
    fileInput.current?.click()
  }

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (!file) return

    if (file.size > maximumFileSize) {
      onError(baseTranslate('errors.max_file_size', { size: maximumFileSize / 1024 / 1024 }))

      return
    }

    onChange(file)
  }

  const handleFileInputClick = ({ target }: MouseEvent<HTMLInputElement>) => {
    const element = target as HTMLInputElement

    element.value = ''
  }

  return (
    <>
      <Button
        size="medium"
        onClick={handleClick}
        icon={
          <Icon
            name={Plus16}
            color="primary-default"
            aria={{ 'aria-hidden': 'true' }}
            testId="kyb-file-select"
          />
        }
        title={title || fileSelectTranslate('upload_document')}
        inline
      />
      <input
        ref={fileInput}
        data-testid="file-uploader"
        disabled={!supportedTypes}
        onChange={handleFileInputChange}
        onClick={handleFileInputClick}
        className="u-hidden"
        type="file"
        accept={supportedTypes}
      />
    </>
  )
}

export default FileSelect
